import type { SheetImageAssetLegacyGeometry } from "building-types/views/sheetGeometryTypes";
import {
  type SheetGeometry,
  type SheetTextGeometry,
  sheetTextGeometryNew,
  type SheetSVGGeometry,
  sheetSVGGeometryNew,
  sheetAssetGeometryNew,
} from "building-types/views/sheetGeometryTypes";
import { PlacementXAnchor, PlacementYAnchor } from "common/utils/textAlignment";
import { mat3Scale } from "geom/mat3";
import type { SheetNodeTransform, GeometrySheetNodeBase } from "lib/house/plans/sheetNode";
import { SheetNodeType } from "lib/house/plans/sheetNode";

export function makeGeometrySheetNode<T extends SheetGeometry>(
  geometry: T,
  transform?: SheetNodeTransform,
): GeometrySheetNodeBase<T> {
  return {
    type: SheetNodeType.GEOMETRY,
    geometry,
    transform,
    handle: geometry.handle,
  };
}

/**
 * Note that this uses top/left anchor alignment by default. This should only be used on tables and built-in sheets.
 */
export function makeTextSheetNode(props: Omit<SheetTextGeometry, "type">, transform?: SheetNodeTransform) {
  return makeGeometrySheetNode(
    sheetTextGeometryNew({ placementXAnchor: PlacementXAnchor.LEFT, placementYAnchor: PlacementYAnchor.TOP, ...props }),
    transform,
  );
}

export function makeSVGSheetNode(
  props: Omit<SheetSVGGeometry, "type" | "inPlanScale" | "scale">,
  transform?: SheetNodeTransform,
) {
  return makeGeometrySheetNode(
    sheetSVGGeometryNew({
      inPlanScale: false,
      ...props,
    }),
    transform,
  );
}

export function makeBuiltInSheetImageSheetNode(
  props: Omit<SheetImageAssetLegacyGeometry, "type" | "assetType" | "asset" | "rotation"> & { url: string },
  transform?: SheetNodeTransform,
) {
  // We flip the y here since the image is being used in a built-in sheet context, where we use a y-down coordinate system.
  // In other contexts, the coordinate system is y-up.
  // This way all of the downstream code can process the image anchor and content as if they were y-up.
  // TODO: Remove this hack once we've unified the coordinate systems.
  return makeGeometrySheetNode(
    sheetAssetGeometryNew({
      assetType: "IMAGE_LEGACY",
      asset: {
        srcType: "IMAGE_LEGACY",
        srcPath: props.url,
      },
      ...props,
      y: -props.y,
      transform: mat3Scale(1, -1),
    }),
    transform,
  );
}
